import { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePaymentReconStore } from "../store";
import { toast } from "react-toastify";
import { useAuthStore } from "store";
import { fetchAllSalebills } from "services/salebill";
import { fetchAllVouchers } from "services/voucher";
import { fetchAllPaymentReconMains } from "services/paymentreconmain";
import { useCreatePaymentReconMain, useUpdatePaymentReconMain, useOnePaymentReconMain } from "http-hooks/paymentreconmain/paymentreconmain";

export const usePaymentRecon = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const authData = useAuthStore((state) => state.authData);
  const agttp = authData?.agttp || 0;

  // Local form state (UI-specific)
  const [reconRecords, setReconRecords] = useState({
    reconid: "",
    previousReconId: "",
    recondate: "",
    remark: "",
    type: agttp === 0 ? "G" : ""
  });

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [reportParameter, setReportParameter] = useState({ value: "D", label: "Default" });
  const [selectedType, setSelectedType] = useState(null);

  // Get shared state from store (documents, modals, etc.)
  const debitDocuments = usePaymentReconStore((state) => state.debitDocuments);
  const creditDocuments = usePaymentReconStore((state) => state.creditDocuments);
  const isLoadingDocs = usePaymentReconStore((state) => state.isLoadingDocs);
  const detailModalOpen = usePaymentReconStore((state) => state.detailModalOpen);
  const selectedDocType = usePaymentReconStore((state) => state.selectedDocType);
  const selectedDocId = usePaymentReconStore((state) => state.selectedDocId);

  // Calculate totals with useMemo - only recalculates when documents change
  const debitTotal = useMemo(() => {
    return debitDocuments
      .filter(doc => doc.isSelected)
      .reduce((sum, doc) => sum + (parseFloat(doc.amount) || 0), 0);
  }, [debitDocuments]);

  const creditTotal = useMemo(() => {
    return creditDocuments
      .filter(doc => doc.isSelected)
      .reduce((sum, doc) => sum + (parseFloat(doc.amount) || 0), 0);
  }, [creditDocuments]);

  // Calculate balance: Debit - Credit
  const balance = useMemo(() => {
    return debitTotal - creditTotal;
  }, [debitTotal, creditTotal]);

  // Fetch existing data in edit mode (placed early so it's available in handleUpdate)
  const { data: existingData, isLoading: isLoadingExisting } = useOnePaymentReconMain(id);

  // Get actions from store
  const setDebitDocuments = usePaymentReconStore((state) => state.setDebitDocuments);
  const setCreditDocuments = usePaymentReconStore((state) => state.setCreditDocuments);
  const setIsLoadingDocs = usePaymentReconStore((state) => state.setIsLoadingDocs);
  const openDetailModal = usePaymentReconStore((state) => state.openDetailModal);
  const closeDetailModal = usePaymentReconStore((state) => state.closeDetailModal);
  const toggleRemarkVisibility = usePaymentReconStore((state) => state.toggleRemarkVisibility);
  const updateDocumentRemark = usePaymentReconStore((state) => state.updateDocumentRemark);
  const updateDocumentAmount = usePaymentReconStore((state) => state.updateDocumentAmount);
  const toggleDocumentSelection = usePaymentReconStore((state) => state.toggleDocumentSelection);
  const resetAllState = usePaymentReconStore((state) => state.resetAllState);

  // Helper to update reconRecords
  const updateReconRecords = useCallback((updates) => {
    setReconRecords(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle input changes
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateReconRecords({ [name]: value });
    },
    [updateReconRecords]
  );

  // Handle type change
  const handleTypeChange = useCallback(
    (val) => {
      setSelectedType(val);
      updateReconRecords({ type: val?.value || "" });
      // Reset vendor/customer when type changes
      setSelectedVendor(null);
      setSelectedCustomer(null);
    },
    [setSelectedType, updateReconRecords, setSelectedVendor, setSelectedCustomer]
  );

  // Handle report parameter change (B2B vs Default for general accounts)
  // B2B = show customers in vendor field, Default = show vendors
  const handleReportParameterChange = useCallback(
    (val) => {
      setReportParameter(val);
      // Clear vendor selection when switching between Default and B2B
      setSelectedVendor(null);
    },
    [setReportParameter, setSelectedVendor]
  );

  // Fetch documents based on current selection
  const handleUpdate = useCallback(async () => {
    // Validation based on agttp and type
    if (agttp === 0) {
      // General accounts: Both vendor and customer required
      if (!selectedCustomer) {
        toast.error("Please select a customer");
        return;
      }
      if (!selectedVendor) {
        toast.error("Please select a vendor");
        return;
      }
    } else if (agttp === 1) {
      // Traders: Type required, then vendor OR customer based on type
      if (!reconRecords.type) {
        toast.error("Please select reconciliation type");
        return;
      }
      if (reconRecords.type === 'S' && !selectedCustomer) {
        toast.error("Please select a customer");
        return;
      }
      if (reconRecords.type === 'P' && !selectedVendor) {
        toast.error("Please select a vendor");
        return;
      }
    }
    
    try {
      setIsLoadingDocs(true);
      
      // === FETCH DEBIT DOCUMENTS ===
      const debitPromises = [];
      
      // 1. Debit Salebills
      const debitSalebillParams = {
        adj: 0,
        packsblid: 0,
        ignore_pl: true,
        salebillno_not_empty: true,
        limit: 10000
      };
      
      if (agttp === 0) {
        debitSalebillParams.cusid = selectedCustomer.ps_Id;
        debitSalebillParams.vendid = selectedVendor.ps_Id;
        debitSalebillParams.billtype = "VDN,OPNDN,DIR,STK,PL,ORD,OPN";
      } else if (reconRecords.type === 'S') {
        debitSalebillParams.cusid = selectedCustomer.ps_Id;
        debitSalebillParams.billtype = "VDN,OPNDN,DIR,STK,PL,ORD,OPN";
        debitSalebillParams.exclude_cash = true;
      } else if (reconRecords.type === 'P') {
        debitSalebillParams.vendid = selectedVendor.ps_Id;
        debitSalebillParams.billtype = "OPNDN,VDN";
        debitSalebillParams.exclude_cash = true;
      }
      
      debitPromises.push(
        fetchAllSalebills(debitSalebillParams).then(res => 
          res.data.map(sb => ({
            doctype: ['VDN', 'OPNDN'].includes(sb.billtype) ? 'VDN' : 
                     reconRecords.type === 'P' ? 'PB' : 'SB',
            docno: sb.salebillno,
            docdate: sb.salebilldate,
            amount: parseFloat(sb.salebillvalue) || 0,
            docid: sb.salebillid,
            mode: 'D',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // 2. Debit Vouchers
      const debitVoucherParams = {
        adj: 0,
        limit: 10000
      };
      
      if (agttp === 0) {
        debitVoucherParams.acdebit = selectedCustomer.ps_Id;
        debitVoucherParams.accredit = selectedVendor.ps_Id;
      } else if (reconRecords.type === 'S') {
        debitVoucherParams.acdebit = selectedCustomer.ps_Id;
      } else if (reconRecords.type === 'P') {
        debitVoucherParams.acdebit = selectedVendor.ps_Id;
      }
      
      debitPromises.push(
        fetchAllVouchers(debitVoucherParams).then(res =>
          res.data.map(v => ({
            doctype: v.trtyp,
            docno: v.docno,
            docdate: v.trdate,
            amount: parseFloat(v.tramount) || 0,
            docid: v.trid,
            mode: 'D',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // 3. Debit Previous Recons (balance > 0)
      const debitReconParams = {
        balance_positive: true,
        adj: 0,
        rectp: reconRecords.type || 'G',
        limit: 10000
      };
      
      if (agttp === 0) {
        debitReconParams.custid = selectedCustomer.ps_Id;
        debitReconParams.vendid = selectedVendor.ps_Id;
      } else if (reconRecords.type === 'S') {
        debitReconParams.custid = selectedCustomer.ps_Id;
      } else if (reconRecords.type === 'P') {
        debitReconParams.vendid = selectedVendor.ps_Id;
      }
      
      if (isEditMode && reconRecords.reconid) {
        debitReconParams.exclude_reconid = reconRecords.reconid;
      }
      
      debitPromises.push(
        fetchAllPaymentReconMains(debitReconParams).then(res =>
          res.data.map(r => ({
            doctype: 'recon',
            docno: r.reconid,
            docdate: r.recdate,
            amount: parseFloat(r.balance) || 0,
            docid: r.reconid,
            mode: 'D',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // === FETCH CREDIT DOCUMENTS ===
      const creditPromises = [];
      
      // 1. Credit Salebills
      const creditSalebillParams = {
        adj: 0,
        packsblid: 0,
        salebillno_not_empty: true,
        limit: 10000
      };
      
      if (agttp === 0) {
        creditSalebillParams.cusid = selectedCustomer.ps_Id;
        creditSalebillParams.vendid = selectedVendor.ps_Id;
        creditSalebillParams.billtype = "VCN,OPNCN";
      } else if (reconRecords.type === 'S') {
        creditSalebillParams.cusid = selectedCustomer.ps_Id;
        creditSalebillParams.billtype = "VCN,OPNCN";
        creditSalebillParams.exclude_cash = true;
      } else if (reconRecords.type === 'P') {
        creditSalebillParams.vendid = selectedVendor.ps_Id;
        creditSalebillParams.billtype = "VCN,OPNCN,DIR,STK,PL,ORD,OPN";
        creditSalebillParams.exclude_cash = true;
      }
      
      creditPromises.push(
        fetchAllSalebills(creditSalebillParams).then(res => 
          res.data.map(sb => ({
            doctype: ['VCN', 'OPNCN'].includes(sb.billtype) ? 'VCN' : 
                     reconRecords.type === 'P' ? 'PB' : 'SB',
            docno: sb.salebillno,
            docdate: sb.salebilldate,
            amount: parseFloat(sb.salebillvalue) || 0,
            docid: sb.salebillid,
            mode: 'C',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // 2. Credit Vouchers
      const creditVoucherParams = {
        adj: 0,
        limit: 10000
      };
      
      if (agttp === 0) {
        creditVoucherParams.accredit = selectedCustomer.ps_Id;
        creditVoucherParams.acdebit = selectedVendor.ps_Id;
      } else if (reconRecords.type === 'S') {
        creditVoucherParams.accredit = selectedCustomer.ps_Id;
      } else if (reconRecords.type === 'P') {
        creditVoucherParams.accredit = selectedVendor.ps_Id;
      }
      
      creditPromises.push(
        fetchAllVouchers(creditVoucherParams).then(res =>
          res.data.map(v => ({
            doctype: v.trtyp,
            docno: v.docno,
            docdate: v.trdate,
            amount: parseFloat(v.tramount) || 0,
            docid: v.trid,
            mode: 'C',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // 3. Credit Previous Recons (balance < 0)
      const creditReconParams = {
        balance_negative: true,
        adj: 0,
        rectp: reconRecords.type || 'G',
        limit: 10000
      };
      
      if (agttp === 0) {
        creditReconParams.custid = selectedCustomer.ps_Id;
        creditReconParams.vendid = selectedVendor.ps_Id;
      } else if (reconRecords.type === 'S') {
        creditReconParams.custid = selectedCustomer.ps_Id;
      } else if (reconRecords.type === 'P') {
        creditReconParams.vendid = selectedVendor.ps_Id;
      }
      
      if (isEditMode && reconRecords.reconid) {
        creditReconParams.exclude_reconid = reconRecords.reconid;
      }
      
      creditPromises.push(
        fetchAllPaymentReconMains(creditReconParams).then(res =>
          res.data.map(r => ({
            doctype: 'recon',
            docno: r.reconid,
            docdate: r.recdate,
            amount: Math.abs(parseFloat(r.balance) || 0),
            docid: r.reconid,
            mode: 'C',
            remark: '',
            isSelected: false,
            showRemark: false
          }))
        ).catch(() => [])
      );
      
      // Execute all promises
      const [debitResults, creditResults] = await Promise.all([
        Promise.all(debitPromises),
        Promise.all(creditPromises)
      ]);
      
      // Merge and sort by date, then docno
      let debitDocs = debitResults.flat().sort((a, b) => {
        const dateCompare = new Date(a.docdate) - new Date(b.docdate);
        return dateCompare !== 0 ? dateCompare : a.docno.localeCompare(b.docno);
      });
      
      let creditDocs = creditResults.flat().sort((a, b) => {
        const dateCompare = new Date(a.docdate) - new Date(b.docdate);
        return dateCompare !== 0 ? dateCompare : a.docno.localeCompare(b.docno);
      });
      
      // In edit mode, add existing documents that might have adj=1 and won't be in the fetched list
      if (isEditMode && existingData && existingData.details) {
        // Process debit documents
        const existingDebitDetails = existingData.details.filter(d => d.mode === 'D');
        existingDebitDetails.forEach(detail => {
          // Check if this document is already in the fetched list
          const existsInFetched = debitDocs.find(doc => doc.docid === detail.docid);
          
          if (existsInFetched) {
            // Mark as selected and update amount/remark
            const index = debitDocs.findIndex(doc => doc.docid === detail.docid);
            debitDocs[index] = {
              ...debitDocs[index],
              isSelected: true,
              amount: parseFloat(detail.amount) || debitDocs[index].amount,
              remark: detail.remark || ''
            };
          } else {
            // Add this document to the list (it has adj=1 so wasn't fetched)
            debitDocs.push({
              doctype: detail.doctype,
              docno: detail.docno,
              docdate: detail.docdt,
              amount: parseFloat(detail.amount) || 0,
              docid: detail.docid,
              mode: 'D',
              remark: detail.remark || '',
              isSelected: true,
              showRemark: false
            });
          }
        });
        
        // Process credit documents
        const existingCreditDetails = existingData.details.filter(d => d.mode === 'C');
        existingCreditDetails.forEach(detail => {
          // Check if this document is already in the fetched list
          const existsInFetched = creditDocs.find(doc => doc.docid === detail.docid);
          
          if (existsInFetched) {
            // Mark as selected and update amount/remark
            const index = creditDocs.findIndex(doc => doc.docid === detail.docid);
            creditDocs[index] = {
              ...creditDocs[index],
              isSelected: true,
              amount: parseFloat(detail.amount) || creditDocs[index].amount,
              remark: detail.remark || ''
            };
          } else {
            // Add this document to the list (it has adj=1 so wasn't fetched)
            creditDocs.push({
              doctype: detail.doctype,
              docno: detail.docno,
              docdate: detail.docdt,
              amount: parseFloat(detail.amount) || 0,
              docid: detail.docid,
              mode: 'C',
              remark: detail.remark || '',
              isSelected: true,
              showRemark: false
            });
          }
        });
        
        // Re-sort after adding existing documents
        debitDocs.sort((a, b) => {
          const dateCompare = new Date(a.docdate) - new Date(b.docdate);
          return dateCompare !== 0 ? dateCompare : a.docno.localeCompare(b.docno);
        });
        
        creditDocs.sort((a, b) => {
          const dateCompare = new Date(a.docdate) - new Date(b.docdate);
          return dateCompare !== 0 ? dateCompare : a.docno.localeCompare(b.docno);
        });
      }
      
      setDebitDocuments(debitDocs);
      setCreditDocuments(creditDocs);
      
      toast.success(`Loaded ${debitDocs.length} debit and ${creditDocs.length} credit documents`);
      
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to fetch documents");
    } finally {
      setIsLoadingDocs(false);
    }
  }, [
    agttp,
    selectedCustomer,
    selectedVendor,
    reconRecords.type,
    reconRecords.reconid,
    isEditMode,
    existingData,
    setIsLoadingDocs,
    setDebitDocuments,
    setCreditDocuments,
  ]);

  // Mutation for creating payment reconciliation
  const createMutation = useCreatePaymentReconMain();

  // Mutation for updating payment reconciliation
  const updateMutation = useUpdatePaymentReconMain();

  // Submit Handler
  const handleSubmit = useCallback(async () => {
    try {
      // Validation: At least one document must be selected
      if (debitTotal === 0 && creditTotal === 0) {
        toast.error("Please select at least one document");
        return;
      }

      // Validation: Required fields
      if (!reconRecords.reconid || !reconRecords.recondate) {
        toast.error("Please fill in all required fields");
        return;
      }

      // For traders (agttp=1), validate based on selected type
      if (agttp === 1) {
        if (!selectedType || !selectedType.value) {
          toast.error("Please select type (Vendor/Customer)");
          return;
        }
        
        if (selectedType.value === 'S' && !selectedCustomer) {
          toast.error("Please select a customer");
          return;
        }
        
        if (selectedType.value === 'P' && !selectedVendor) {
          toast.error("Please select a vendor");
          return;
        }
      }

      // For general accounts (agttp=0), validate vendor/customer
      if (agttp === 0) {
        if (!selectedCustomer) {
          toast.error("Please select a customer");
          return;
        }
        if (!selectedVendor) {
          toast.error("Please select a vendor");
          return;
        }
      }

      // Collect selected documents from both debit and credit
      const selectedDocuments = [];
      
      // Debit documents
      debitDocuments
        .filter(doc => doc.isSelected)
        .forEach(doc => {
          selectedDocuments.push({
            docid: doc.docid,
            doctype: doc.doctype,
            docno: doc.docno,
            docdt: doc.docdate,
            amount: doc.amount,
            mode: 'D', // Debit
            remark: doc.remark || ''
          });
        });
      
      // Credit documents
      creditDocuments
        .filter(doc => doc.isSelected)
        .forEach(doc => {
          selectedDocuments.push({
            docid: doc.docid,
            doctype: doc.doctype,
            docno: doc.docno,
            docdt: doc.docdate,
            amount: doc.amount,
            mode: 'C', // Credit
            remark: doc.remark || ''
          });
        });

      // Prepare submission data
      const submissionData = {
        reconid: reconRecords.reconid,
        custid: selectedCustomer?.ps_Id || '',
        vendid: selectedVendor?.ps_Id || '',
        remark: reconRecords.remark || '',
        recdate: reconRecords.recondate,
        rectp: agttp === 0 ? 'G' : (selectedType?.value || ''),
        debitvalue: debitTotal,
        creditvalue: creditTotal,
        selectedDocuments
      };

      // Call create or update API based on mode
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: reconRecords.reconid, data: submissionData });
      } else {
        await createMutation.mutateAsync(submissionData);
      }
      
      // Navigate on success (toast is handled by service layer)
      navigate('/paymentrecon');
      
    } catch (error) {
      console.error("Error submitting reconciliation:", error);
      // Error toast is handled by service layer
    }
  }, [
    reconRecords,
    selectedVendor,
    selectedCustomer,
    selectedType,
    debitDocuments,
    creditDocuments,
    debitTotal,
    creditTotal,
    agttp,
    isEditMode,
    createMutation,
    updateMutation,
    navigate
  ]);

  return {
    // State
    reconRecords,
    selectedVendor,
    selectedCustomer,
    reportParameter,
    selectedType,
    debitDocuments,
    creditDocuments,
    debitTotal,
    creditTotal,
    balance,
    isLoadingDocs,
    detailModalOpen,
    selectedDocType,
    selectedDocId,
    agttp,
    isEditMode,
    existingData,
    isLoadingExisting,

    // Actions - Basic
    setReconRecords,
    updateReconRecords,
    handleInputChange,
    setSelectedVendor,
    setSelectedCustomer,
    setReportParameter,
    handleReportParameterChange,
    setSelectedType,
    handleTypeChange,
    
    // Actions - Documents
    handleUpdate,
    toggleDocumentSelection,
    updateDocumentAmount,
    updateDocumentRemark,
    
    // Actions - Modal
    openDetailModal,
    closeDetailModal,
    
    // Actions - Remarks
    toggleRemarkVisibility,
    
    // Actions - Submit
    handleSubmit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    
    // Actions - Reset
    resetAllState,
  };
};

